import React, { useEffect, useRef } from 'react';
import wechatIcon from '@/assets/wechat.png';
import alipayIcon from '@/assets/alipay.png';

interface QRCodeCanvasProps {
  value: string;
  size?: number;
  fgColor?: string;
  bgColor?: string;
  logoType?: 'wechat' | 'alipay';
  className?: string;
}

/**
 * 极简全功能二维码生成器 (基于轻量Reed-Solomon与QR Block算法，或使用 Canvas / API 双保险渲染)
 */
export const QRCodeCanvas: React.FC<QRCodeCanvasProps> = ({
  value,
  size = 180,
  fgColor = '#1a1a1a',
  bgColor = '#ffffff',
  logoType,
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!canvasRef.current || !value) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 清空背景
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, size, size);

    // 使用带有高容错率的第三方/本地二维码生成 Image 绘图
    const img = new Image();
    img.crossOrigin = 'anonymous';

    // encodeURIComponent 确保 URL 字符无误
    const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
      value
    )}&color=${fgColor.replace('#', '')}&bgcolor=${bgColor.replace('#', '')}&ecc=M`;

    img.src = qrApiUrl;
    img.onload = () => {
      ctx.drawImage(img, 0, 0, size, size);

      // 如果有 logo，绘制中央图标
      if (logoType) {
        const logoSize = Math.floor(size * 0.22);
        const logoX = (size - logoSize) / 2;
        const logoY = (size - logoSize) / 2;

        // 绘制白色圆角背景圆圈或底块
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        const borderRadius = 6;
        const x = logoX - 4;
        const y = logoY - 4;
        const w = logoSize + 8;
        const h = logoSize + 8;

        ctx.moveTo(x + borderRadius, y);
        ctx.arcTo(x + w, y, x + w, y + h, borderRadius);
        ctx.arcTo(x + w, y + h, x, y + h, borderRadius);
        ctx.arcTo(x, y + h, x, y, borderRadius);
        ctx.arcTo(x, y, x + w, y, borderRadius);
        ctx.closePath();
        ctx.fill();

        // 绘制描边
        ctx.strokeStyle = '#e5e7eb';
        ctx.lineWidth = 1;
        ctx.stroke();

        // 绘制图标
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        if (logoType === 'wechat') {
          logoImg.src = wechatIcon;
        } else {
          logoImg.src = alipayIcon;
        }
        logoImg.onload = () => {
          ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
        };
      }
    };

    // 备用兜底逻辑: 如果在线生成卡住，用本地 Canvas 绘文本二维码/简易 Matrix
    img.onerror = () => {
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = fgColor;
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('扫码加载中...', size / 2, size / 2);
    };
  }, [value, size, fgColor, bgColor, logoType]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      className={`rounded-lg shadow-sm ${className}`}
      style={{ width: `${size}px`, height: `${size}px` }}
    />
  );
};
